import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { loginWithLocalUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem("admin_token", res.data.token);
      localStorage.setItem("admin_name", res.data.name);
      localStorage.setItem("admin_role", res.data.role);
      localStorage.setItem("admin_permissions", JSON.stringify(res.data.permissions || []));
      toast.success("Welcome back, " + res.data.name);
      // Hydrate AuthContext with app_token so learner routes (/intake, /my-plans) are accessible
      if (res.data.app_token && res.data.user) {
        loginWithLocalUser(res.data.user, res.data.app_token);
      }
      const perms = res.data.permissions || [];
      navigate(perms.length === 0 ? "/my-plans" : "/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF8F3] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <button onClick={() => navigate("/")} className="inline-flex items-center gap-3 mb-6" data-testid="logo-home-link-admin">
            <img src="/technofocus-logo-white.png" alt="Technofocus" className="h-7 brightness-0 invert" style={{ filter: 'none' }} />
            <div className="w-px h-6 bg-[#194449]/20" />
            <span className="text-xl font-semibold text-[#091F2C] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>MyGoals</span>
          </button>
          <h1 className="text-2xl font-semibold text-[#091F2C] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Admin Portal
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Sign in to manage resources and view analytics</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-8">
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <Label className="text-sm font-medium text-[#091F2C] mb-2 block">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@mygoals.com"
                className="h-12 bg-white"
                required
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#091F2C] mb-2 block">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="h-12 bg-white"
                required
                data-testid="admin-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#194449] text-white hover:bg-[#07641D] rounded-full h-12 font-medium shadow-md hover:shadow-lg transition-all duration-300"
              data-testid="admin-login-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Sign In
                </>
              )}
            </Button>
          </form>
        </div>

        <p className="text-center text-xs text-[#64748B] mt-6">
          &copy; {new Date().getFullYear()} Technofocus &mdash; MyGoals
        </p>
      </div>
    </div>
  );
}
